import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../types';
import { STORAGE_KEYS } from '../utils/constants';
import storage from '../utils/storage';

// VSNP-704: storage entries persist without expiration. Auth lifetime is
// driven by useIdleTimeout (activity-based) + backend token expiry (8h,
// proactively refreshed). The old 20-minute fixed-clock TTL on every
// storage entry was the cause of "session keeps timing out while I'm
// using the UI".
const STORAGE_NO_EXPIRY = 0;

interface AuthState {
  user: User | null;
  vsnToken: string | null; // VSN+ session token (the only token we hold)
  vsnTokenExpiresAt: string | null;
  // VSNP-721: absolute logout time. The hard cap on session lifetime,
  // independent of activity and unaffected by token refresh. Null on
  // remember-me sessions (opted out by user choice). Used by
  // useIdleTimeout to arm a second timer alongside the activity-based
  // idle timer.
  vsnAbsoluteExpiresAt: string | null;
  isAuthenticated: boolean;
  vpnUserBlocked: boolean; // Set when a VPN-only user hits the web login

  // Assumed identity stash (original SuperAdmin state)
  originalUser: User | null;
  originalVsnToken: string | null;
  originalVsnTokenExpiresAt: string | null;
  originalVsnAbsoluteExpiresAt: string | null;

  setUser: (user: User) => void;
  setVSNAuth: (token: string, expiresAt: string, user: User, absoluteExpiresAt?: string | null) => void;
  setVpnUserBlocked: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
  getVSNToken: () => string | null;

  // Assumed identity methods
  isAssumedIdentity: () => boolean;
  assumeIdentity: (token: string, expiresAt: string, user: User, absoluteExpiresAt?: string | null) => void;
  returnToOriginal: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      vsnToken: null,
      vsnTokenExpiresAt: null,
      vsnAbsoluteExpiresAt: null,
      isAuthenticated: false,
      vpnUserBlocked: false,

      // Stash fields
      originalUser: null,
      originalVsnToken: null,
      originalVsnTokenExpiresAt: null,
      originalVsnAbsoluteExpiresAt: null,

      setUser: (user: User) => {
        storage.session.set(STORAGE_KEYS.USER_DATA, user, STORAGE_NO_EXPIRY);
        set({ user });
      },

      setVSNAuth: (token: string, expiresAt: string, user: User, absoluteExpiresAt?: string | null) => {
        storage.session.set('vsn_token', token, STORAGE_NO_EXPIRY);
        storage.session.set('vsn_token_expires', expiresAt, STORAGE_NO_EXPIRY);
        storage.session.set(STORAGE_KEYS.USER_DATA, user, STORAGE_NO_EXPIRY);
        if (absoluteExpiresAt) {
          storage.session.set('vsn_absolute_expires', absoluteExpiresAt, STORAGE_NO_EXPIRY);
        } else {
          storage.session.remove('vsn_absolute_expires');
        }

        set({
          vsnToken: token,
          vsnTokenExpiresAt: expiresAt,
          vsnAbsoluteExpiresAt: absoluteExpiresAt ?? null,
          user,
          isAuthenticated: true,
          vpnUserBlocked: false,
        });
      },

      setVpnUserBlocked: () => {
        set({ vpnUserBlocked: true, isAuthenticated: false });
      },

      logout: () => {
        storage.session.remove(STORAGE_KEYS.USER_DATA);
        storage.session.remove(STORAGE_KEYS.MENU_DATA);
        storage.session.remove(STORAGE_KEYS.TENANT_VALUE);
        storage.session.remove('vsn_token');
        storage.session.remove('vsn_token_expires');
        storage.session.remove('vsn_absolute_expires');

        set({
          user: null,
          vsnToken: null,
          vsnTokenExpiresAt: null,
          vsnAbsoluteExpiresAt: null,
          isAuthenticated: false,
          originalUser: null,
          originalVsnToken: null,
          originalVsnTokenExpiresAt: null,
          originalVsnAbsoluteExpiresAt: null,
          vpnUserBlocked: false,
        });
      },

      // Back-compat alias — callers outside auth still use getAccessToken.
      // No re-stamping needed: storage entries persist without expiration
      // (auth lifetime is governed by useIdleTimeout, not storage TTL).
      getAccessToken: () => storage.session.get<string>('vsn_token'),

      getVSNToken: () => storage.session.get<string>('vsn_token'),

      isAssumedIdentity: () => {
        return !!get().originalUser;
      },

      assumeIdentity: (token: string, expiresAt: string, user: User, absoluteExpiresAt?: string | null) => {
        const state = get();

        // Stash current SuperAdmin state
        set({
          originalUser: state.user,
          originalVsnToken: state.vsnToken,
          originalVsnTokenExpiresAt: state.vsnTokenExpiresAt,
          originalVsnAbsoluteExpiresAt: state.vsnAbsoluteExpiresAt,
        });

        // Swap to assumed identity
        storage.session.set('vsn_token', token, STORAGE_NO_EXPIRY);
        storage.session.set('vsn_token_expires', expiresAt, STORAGE_NO_EXPIRY);
        storage.session.set(STORAGE_KEYS.USER_DATA, user, STORAGE_NO_EXPIRY);
        if (absoluteExpiresAt) {
          storage.session.set('vsn_absolute_expires', absoluteExpiresAt, STORAGE_NO_EXPIRY);
        } else {
          storage.session.remove('vsn_absolute_expires');
        }

        set({
          vsnToken: token,
          vsnTokenExpiresAt: expiresAt,
          vsnAbsoluteExpiresAt: absoluteExpiresAt ?? null,
          user,
        });
      },

      returnToOriginal: () => {
        const state = get();
        if (!state.originalUser || !state.originalVsnToken) return;

        // Restore SuperAdmin state
        storage.session.set('vsn_token', state.originalVsnToken, STORAGE_NO_EXPIRY);
        storage.session.set('vsn_token_expires', state.originalVsnTokenExpiresAt || '', STORAGE_NO_EXPIRY);
        storage.session.set(STORAGE_KEYS.USER_DATA, state.originalUser, STORAGE_NO_EXPIRY);
        if (state.originalVsnAbsoluteExpiresAt) {
          storage.session.set('vsn_absolute_expires', state.originalVsnAbsoluteExpiresAt, STORAGE_NO_EXPIRY);
        } else {
          storage.session.remove('vsn_absolute_expires');
        }

        set({
          vsnToken: state.originalVsnToken,
          vsnTokenExpiresAt: state.originalVsnTokenExpiresAt,
          vsnAbsoluteExpiresAt: state.originalVsnAbsoluteExpiresAt,
          user: state.originalUser,
          originalUser: null,
          originalVsnToken: null,
          originalVsnTokenExpiresAt: null,
          originalVsnAbsoluteExpiresAt: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
